import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	ArtifactResponseJSON,
	CreateArtifactRequest,
	ListArtifactsResponseJSON,
	RenderRequest,
	UpdateArtifactRequest,
} from "@performyard/service-contracts";

import {
	createArtifact,
	getArtifact,
	getArtifacts,
	renderArtifact,
	updateArtifact,
} from "../../services/defs/reporting-artifacts";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

// Query keys
export const reportingArtifactsKeys = {
	all: ["ReportingArtifacts"] as const,
	list: () => [...reportingArtifactsKeys.all, "list"] as const,
	detail: (id: string) => [...reportingArtifactsKeys.all, "detail", id] as const,
};

// List artifacts
export const useArtifacts = (options?: PYUseQueryOptions<ListArtifactsResponseJSON>) => {
	return useQuery({
		queryKey: reportingArtifactsKeys.list(),
		queryFn: () => getArtifacts(),
		meta: {
			errorMessage: "There was an error getting the report artifacts.",
		},
		...options,
	});
};

// Get single artifact
export const useArtifact = (id: string, options?: PYUseQueryOptions<ArtifactResponseJSON>) => {
	return useQuery({
		queryKey: reportingArtifactsKeys.detail(id),
		queryFn: () => getArtifact(id),
		meta: {
			errorMessage: "There was an error getting the report artifact.",
		},
		...options,
		// ANDed with the caller's own enabled (e.g. a permission/FF gate) so a passed-in `true`
		// can never re-enable the query for a blank id.
		enabled: (options?.enabled ?? true) && !!id,
	});
};

// Create artifact
export const useCreateArtifact = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateArtifactRequest) => createArtifact(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: reportingArtifactsKeys.all });
		},
		...options,
	});
};

// Update artifact
export const useUpdateArtifact = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateArtifactRequest }) => updateArtifact(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: reportingArtifactsKeys.all });
		},
		...options,
	});
};

// Render artifact (re-run query or return stored snapshot)
export const useRenderArtifact = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: (body: RenderRequest) => renderArtifact(body),
		...options,
	});
};
